import React, { useState, useEffect } from 'react';
import { X, Wine, User, Phone, Mail, MapPin, Calendar, Truck, FileText, Plus, TrendingUp } from 'lucide-react';
import { promoSummerOrderService } from '../../../services/promoSummerOrderService';
import { providerService } from '../../../services/providerService';

export default function PromoOrderForm({ onClose, onSaved, initialClientId, initialClientName }) {
  const [form, setForm] = useState({
    clientName: initialClientName || '',
    clientPhone: '',
    clientEmail: '',
    barrelQuantity: 1,
    deliveryDate: '',
    deliveryAddress: '',
    deliveryCity: '',
    deliveryPostalCode: '',
    notes: '',
  });

  // Provider selection state
  const [providers, setProviders] = useState([]);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerProducts, setProviderProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loadingProviders, setLoadingProviders] = useState(true);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const qty = Number(form?.barrelQuantity) || 1;

  // Use product prices if a product is selected, otherwise use defaults
  const unitPromoPrice = 160;
  const unitDeposit = 50;
  const unitBalance = 110;
  const unitProviderCost = selectedProduct ? selectedProduct?.purchasePrice : 30;
  const unitClientPrice = selectedProduct ? selectedProduct?.clientPrice : 50;
  const unitAdtMargin = selectedProduct ? selectedProduct?.adtMargin : 20;

  const totalPromo = qty * unitPromoPrice;
  const totalDeposit = qty * unitDeposit;
  const totalBalance = qty * unitBalance;
  const totalProviderCost = qty * unitProviderCost;
  const totalAdtMargin = qty * unitAdtMargin;

  // Load active providers on mount
  useEffect(() => {
    const loadProviders = async () => {
      try {
        const data = await providerService?.getAll({ activeOnly: true });
        setProviders(data?.filter(p => p?.status === 'actif'));
      } catch (e) {
        // silently fail, user can still type manually
      } finally {
        setLoadingProviders(false);
      }
    };
    loadProviders();
  }, []);

  // When provider changes, load their products
  const handleProviderChange = async (providerId) => {
    setSelectedProviderId(providerId);
    setSelectedProductId('');
    setSelectedProduct(null);

    if (!providerId) {
      setSelectedProvider(null);
      setProviderProducts([]);
      return;
    }

    const provider = providers?.find(p => p?.id === providerId);
    setSelectedProvider(provider || null);

    try {
      const products = await providerService?.getActiveProductsByProvider(providerId);
      setProviderProducts(products);
      // Auto-select if only one product
      if (products?.length === 1) {
        setSelectedProductId(products?.[0]?.id);
        setSelectedProduct(products?.[0]);
      }
    } catch (e) {
      setProviderProducts([]);
    }
  };

  const handleProductChange = (productId) => {
    setSelectedProductId(productId);
    const product = providerProducts?.find(p => p?.id === productId);
    setSelectedProduct(product || null);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.clientName?.trim()) {
      setError('Le nom du client est obligatoire');
      return;
    }
    if (qty < 1) {
      setError('La quantité doit être au moins 1');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const newOrder = await promoSummerOrderService?.create({
        ...form,
        clientId: initialClientId || null,
        barrelQuantity: qty,
        providerId: selectedProviderId || null,
        providerProductId: selectedProductId || null,
        selectedProductName: selectedProduct?.productName || null,
        selectedPurchasePrice: selectedProduct ? selectedProduct?.purchasePrice : null,
        selectedClientPrice: selectedProduct ? selectedProduct?.clientPrice : null,
        selectedAdtMargin: selectedProduct ? selectedProduct?.adtMargin : null,
        // Keep legacy fields for backward compat
        providerName: selectedProvider?.name || null,
        providerPhone: selectedProvider?.phone || null,
      });
      onSaved?.(newOrder);
    } catch (err) {
      setError(err?.message || 'Erreur lors de la création de la commande');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
              <Wine className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Nouvelle commande promo</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Barrique 270L — Promo Été 2026</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Client Info */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <User className="h-4 w-4" /> Informations client
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Nom complet <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form?.clientName}
                  onChange={(e) => handleChange('clientName', e?.target?.value)}
                  placeholder="Nom du client"
                  className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  <Phone className="inline h-3 w-3 mr-1" />Téléphone
                </label>
                <input
                  type="tel"
                  value={form?.clientPhone}
                  onChange={(e) => handleChange('clientPhone', e?.target?.value)}
                  placeholder="06 XX XX XX XX"
                  className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  <Mail className="inline h-3 w-3 mr-1" />Email
                </label>
                <input
                  type="email"
                  value={form?.clientEmail}
                  onChange={(e) => handleChange('clientEmail', e?.target?.value)}
                  placeholder="email@exemple.com"
                  className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Order Details */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <Wine className="h-4 w-4" /> Détails commande
            </h3>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Nombre de barriques 270L <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                max="50"
                value={form?.barrelQuantity}
                onChange={(e) => handleChange('barrelQuantity', e?.target?.value)}
                className="w-32 px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Provider selection */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <Truck className="h-4 w-4" /> Prestataire livreur
            </h3>

            <div className="space-y-3">
              {/* Provider dropdown */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Sélectionner un prestataire
                </label>
                <div className="flex gap-2">
                  <select
                    value={selectedProviderId}
                    onChange={(e) => handleProviderChange(e?.target?.value)}
                    className="flex-1 px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  >
                    <option value="">— Choisir un prestataire —</option>
                    {loadingProviders ? (
                      <option disabled>Chargement...</option>
                    ) : (
                      providers?.map(p => (
                        <option key={p?.id} value={p?.id}>{p?.name}{p?.phone ? ` · ${p?.phone}` : ''}</option>
                      ))
                    )}
                  </select>
                  <a
                    href="/carton-suppliers-management"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-3 py-2 text-sm border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 whitespace-nowrap"
                    title="Ajouter un prestataire"
                  >
                    <Plus className="h-4 w-4" />
                    <span className="hidden sm:inline">Ajouter</span>
                  </a>
                </div>
              </div>

              {/* Provider info display */}
              {selectedProvider && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-blue-800 dark:text-blue-300">{selectedProvider?.name}</span>
                    {selectedProvider?.phone && (
                      <span className="text-blue-600 dark:text-blue-400 flex items-center gap-1">
                        <Phone className="h-3 w-3" />{selectedProvider?.phone}
                      </span>
                    )}
                    {selectedProvider?.interventionZone && (
                      <span className="text-blue-600 dark:text-blue-400 text-xs">Zone : {selectedProvider?.interventionZone}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Product selection */}
              {selectedProviderId && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Produit proposé par ce prestataire
                  </label>
                  {providerProducts?.length === 0 ? (
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 text-sm text-yellow-700 dark:text-yellow-400">
                      Aucun produit enregistré pour ce prestataire.{' '}
                      <a href="/carton-suppliers-management" target="_blank" rel="noopener noreferrer" className="underline font-medium">
                        Ajouter des produits
                      </a>
                    </div>
                  ) : (
                    <select
                      value={selectedProductId}
                      onChange={(e) => handleProductChange(e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    >
                      <option value="">— Choisir un produit —</option>
                      {providerProducts?.map(p => (
                        <option key={p?.id} value={p?.id}>
                          {p?.productName} · achat {p?.purchasePrice}€ · client {p?.clientPrice}€ · marge {p?.adtMargin}€
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Price summary */}
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-3 flex items-center gap-1">
              <TrendingUp className="h-3.5 w-3.5" /> Récapitulatif tarifaire
            </p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              <span className="text-gray-600 dark:text-gray-400">Total promo ({qty} × {unitPromoPrice}€)</span>
              <span className="font-semibold text-gray-900 dark:text-white">{totalPromo}€</span>
              <span className="text-gray-600 dark:text-gray-400">Acompte livraison ({qty} × {unitDeposit}€)</span>
              <span className="font-semibold text-green-600">{totalDeposit}€</span>
              <span className="text-gray-600 dark:text-gray-400">Solde collecte ({qty} × {unitBalance}€)</span>
              <span className="font-semibold text-blue-600">{totalBalance}€</span>
              <span className="text-gray-600 dark:text-gray-400">
                Coût prestataire ({qty} × {unitProviderCost}€)
                {selectedProduct && <span className="ml-1 text-xs text-amber-600">↑ tarif prestataire</span>}
              </span>
              <span className="font-semibold text-orange-600">{totalProviderCost}€</span>
              <span className="text-gray-600 dark:text-gray-400">
                Marge/reversement ADT ({qty} × {unitAdtMargin}€)
                {selectedProduct && <span className="ml-1 text-xs text-teal-600">↑ calculée</span>}
              </span>
              <span className="font-semibold text-teal-600">{totalAdtMargin}€</span>
            </div>
            {selectedProduct && (
              <div className="mt-2 pt-2 border-t border-amber-200 dark:border-amber-700 text-xs text-amber-700 dark:text-amber-400">
                Produit : <strong>{selectedProduct?.productName}</strong> — Prix client livraison : {unitClientPrice}€/unité
              </div>
            )}
          </div>

          {/* Delivery */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <MapPin className="h-4 w-4" /> Livraison des barriques
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  <Calendar className="inline h-3 w-3 mr-1" />Date de livraison
                </label>
                <input
                  type="date"
                  value={form?.deliveryDate}
                  onChange={(e) => handleChange('deliveryDate', e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Code postal</label>
                <input
                  type="text"
                  value={form?.deliveryPostalCode}
                  onChange={(e) => handleChange('deliveryPostalCode', e?.target?.value)}
                  placeholder="75001"
                  className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Ville</label>
                <input
                  type="text"
                  value={form?.deliveryCity}
                  onChange={(e) => handleChange('deliveryCity', e?.target?.value)}
                  placeholder="Paris"
                  className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Adresse</label>
                <input
                  type="text"
                  value={form?.deliveryAddress}
                  onChange={(e) => handleChange('deliveryAddress', e?.target?.value)}
                  placeholder="12 rue de la Paix"
                  className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 flex items-center gap-1">
              <FileText className="h-3 w-3" /> Notes internes
            </label>
            <textarea
              value={form?.notes}
              onChange={(e) => handleChange('notes', e?.target?.value)}
              rows={3}
              placeholder="Informations complémentaires..."
              className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2 border-t border-gray-200 dark:border-slate-700">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
            >
              {saving ? 'Création...' : 'Créer la commande'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
